import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { PropTypes } from '../../../utils/propTypes';
import styles from './RadioGroup.module.css';

/**
 * RadioGroup + RadioGroupItem — a single-select set of radio buttons,
 * reimplemented in Athena's idiom (JSX + CSS Module + 2.0 tokens). Standalone
 * (distinct from the DropdownMenu radio). Flat styling; the per-theme box bg +
 * invalid ring use [data-theme="light"] overrides (mirrors Checkbox). Preview-only.
 *
 *   <RadioGroup value onValueChange>
 *     <RadioGroupItem value="a" id="a" /> <Label htmlFor="a">A</Label>
 *   </RadioGroup>
 */

const RadioGroupContext = createContext(null);
const useRadioGroup = () => {
  const ctx = useContext(RadioGroupContext);
  if (!ctx) throw new Error('RadioGroupItem must be used within <RadioGroup>');
  return ctx;
};

const RadioGroup = ({
  value: controlledValue,
  defaultValue = null,
  onValueChange,
  disabled = false,
  className = '',
  children,
  ...props
}) => {
  const isControlled = controlledValue !== undefined;
  const [internal, setInternal] = useState(defaultValue);
  const value = isControlled ? controlledValue : internal;

  const setValue = useCallback(
    (next) => {
      if (!isControlled) setInternal(next);
      onValueChange?.(next);
    },
    [isControlled, onValueChange]
  );

  const ctx = useMemo(() => ({ value, setValue, disabled }), [value, setValue, disabled]);

  return (
    <RadioGroupContext.Provider value={ctx}>
      <div role="radiogroup" className={`${styles.group} ${className}`} {...props}>
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
};

const RadioGroupItem = ({ value, id, disabled = false, className = '', 'aria-invalid': ariaInvalid, ...props }) => {
  const { value: selected, setValue, disabled: groupDisabled } = useRadioGroup();
  const checked = selected === value;
  const isDisabled = disabled || groupDisabled;

  // Roving selection with arrow keys among enabled items in the group.
  const onKeyDown = (e) => {
    if (!['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(e.key)) return;
    e.preventDefault();
    const group = e.currentTarget.closest('[role="radiogroup"]');
    if (!group) return;
    const items = Array.from(group.querySelectorAll('[role="radio"]:not(:disabled)'));
    const idx = items.indexOf(e.currentTarget);
    const dir = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1;
    const next = items[(idx + dir + items.length) % items.length];
    if (next) {
      next.focus();
      next.click();
    }
  };

  return (
    <button
      type="button"
      role="radio"
      id={id}
      aria-checked={checked}
      aria-invalid={ariaInvalid}
      data-state={checked ? 'checked' : 'unchecked'}
      disabled={isDisabled}
      className={`${styles.item} ${className}`}
      onClick={() => !isDisabled && setValue(value)}
      onKeyDown={onKeyDown}
      {...props}
    >
      {checked && <span className={styles.dot} aria-hidden="true" />}
    </button>
  );
};

RadioGroup.propTypes = {
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  onValueChange: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};
RadioGroupItem.propTypes = {
  value: PropTypes.string,
  id: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  'aria-invalid': PropTypes.bool,
};

export default RadioGroup;
export { RadioGroup, RadioGroupItem };
